import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    ScrollView,
    TouchableOpacity,
    StyleSheet,
    ActivityIndicator,
    Alert,
    Dimensions,
    TextInput,
    Modal
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { H1, H2, H3, Body1, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';
import { fetchLoanEligibility, fetchMyLoans, applyForLoan, repayLoan } from '../../api/loans';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const LoanScreen = ({ navigation }) => {
    const insets = useSafeAreaInsets();
    const [loading, setLoading] = useState(true);
    const [eligibility, setEligibility] = useState(null);
    const [loans, setLoans] = useState([]);
    const [applying, setApplying] = useState(false);

    // Application state
    const [amount, setAmount] = useState('');
    const [purpose, setPurpose] = useState('');
    const [duration, setDuration] = useState('1');

    // Repayment state
    const [repayAmount, setRepayAmount] = useState('');
    const [repayPhone, setRepayPhone] = useState('');
    const [showRepayModal, setShowRepayModal] = useState(false);
    const [repaying, setRepaying] = useState(false);
    const [user, setUser] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [eligData, loansData, userDataStr] = await Promise.all([
                fetchLoanEligibility(),
                fetchMyLoans(),
                AsyncStorage.getItem('user')
            ]);
            setEligibility(eligData);
            setLoans(loansData);
            if (userDataStr) {
                const userData = JSON.parse(userDataStr);
                setUser(userData);
                setRepayPhone(userData.phone || '');
            }
        } catch (error) {
            console.error('Error loading loan data:', error);
            Alert.alert('Error', 'Failed to load loan information.');
        } finally {
            setLoading(false);
        }
    };

    const handleApply = async () => {
        if (!amount || parseFloat(amount) <= 0) {
            Alert.alert('Invalid Amount', 'Please enter a valid loan amount.');
            return;
        }

        if (parseFloat(amount) > eligibility.maxAmount) {
            Alert.alert('Limit Exceeded', `Your current limit is KSH ${eligibility.maxAmount.toLocaleString()}.`);
            return;
        }

        setApplying(true);
        try {
            await applyForLoan({
                amount: parseFloat(amount),
                duration_months: parseInt(duration),
                purpose
            });
            Alert.alert('Success', 'Your loan application has been submitted and is pending approval.');
            setAmount('');
            setPurpose('');
            loadData();
        } catch (error) {
            Alert.alert('Application Failed', error.response?.data?.error || 'Could not submit application.');
        } finally {
            setApplying(false);
        }
    };

    const handleRepay = async () => {
        const activeLoan = loans.find(l => ['active', 'approved'].includes(l.status));
        if (!activeLoan) return;

        if (!repayAmount || parseFloat(repayAmount) <= 0) {
            Alert.alert('Invalid Amount', 'Please enter a valid amount.');
            return;
        }

        if (!repayPhone || repayPhone.length < 10) {
            Alert.alert('Invalid Phone', 'Please enter a valid M-Pesa phone number.');
            return;
        }

        setRepaying(true);
        try {
            await repayLoan(activeLoan.id, parseFloat(repayAmount), repayPhone);
            Alert.alert(
                'Payment Initiated',
                'An M-Pesa STK Push has been sent to your phone. Please enter your PIN to complete the payment.'
            );
            setShowRepayModal(false);
            setRepayAmount('');

            // Reload data after a short delay to check for updates
            setTimeout(loadData, 5000);
        } catch (error) {
            Alert.alert('Payment Failed', error.response?.data?.error || 'Could not initiate repayment.');
        } finally {
            setRepaying(false);
        }
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#10b981" />
            </View>
        );
    }

    const activeLoan = loans.find(l => ['active', 'pending', 'approved'].includes(l.status));

    return (
        <View style={styles.container}>
            <StatusBar style="light" />

            {/* Header */}
            <LinearGradient
                colors={['#059669', '#10b981']}
                style={[styles.header, { paddingTop: insets.top + 10 }]}
            >
                <View style={styles.headerTop}>
                    <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
                        <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
                    </TouchableOpacity>
                    <H2 style={styles.headerTitle}>Youth Loans</H2>
                    <View style={{ width: 40 }} />
                </View>

                <View style={styles.eligibilityCard}>
                    <Caption style={styles.eligLabel}>CREDIT STATUS</Caption>
                    <H1 style={styles.eligStatus}>{eligibility?.status || 'N/A'}</H1>
                    <View style={styles.limitBox}>
                        <Caption style={styles.limitLabel}>MAX LOAN LIMIT</Caption>
                        <H2 style={styles.limitValue}>KSH {eligibility?.maxAmount.toLocaleString()}</H2>
                    </View>
                </View>
            </LinearGradient>

            <ScrollView
                style={styles.content}
                contentContainerStyle={{ paddingBottom: 40 }}
                showsVerticalScrollIndicator={false}
            >
                {/* Wisdom/Advice Panel */}
                <View style={styles.advicePanel}>
                    <View style={styles.adviceIcon}>
                        <Ionicons name="bulb" size={24} color="#F59E0B" />
                    </View>
                    <View style={styles.adviceTextContainer}>
                        <H3 style={styles.adviceTitle}>Smart Credit Advice</H3>
                        <Body2 style={styles.adviceText}>{eligibility?.advice}</Body2>
                    </View>
                </View>

                {/* score breakdown */}
                <View style={styles.section}>
                    <H3 style={styles.sectionTitle}>Credit Breakdown</H3>
                    <View style={styles.breakdownGrid}>
                        <ScoreItem label="Savings" score={eligibility?.breakdown.savings} icon="wallet-outline" color="#6366F1" />
                        <ScoreItem label="Points" score={eligibility?.breakdown.points} icon="star-outline" color="#F59E0B" />
                        <ScoreItem label="Market" score={eligibility?.breakdown.marketplace} icon="cart-outline" color="#10B981" />
                        <ScoreItem label="Level" score={eligibility?.breakdown.level} icon="trending-up-outline" color="#8B5CF6" />
                        <ScoreItem label="Activity" score={eligibility?.breakdown.engagement} icon="flash-outline" color="#EC4899" />
                    </View>
                </View>

                {/* loan application / Active loan */}
                {activeLoan ? (
                    <View style={styles.section}>
                        <H3 style={styles.sectionTitle}>Active/Pending Loan</H3>
                        <View style={styles.loanCard}>
                            <View style={styles.loanCardHeader}>
                                <View>
                                    <Caption style={styles.loanLabel}>Amount Due</Caption>
                                    <H2 style={styles.loanValue}>KSH {parseFloat(activeLoan.balance).toLocaleString()}</H2>
                                </View>
                                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(activeLoan.status) + '20' }]}>
                                    <Text style={[styles.statusText, { color: getStatusColor(activeLoan.status) }]}>
                                        {activeLoan.status.toUpperCase()}
                                    </Text>
                                </View>
                            </View>
                            <View style={styles.loanDetails}>
                                <View style={styles.detailRow}>
                                    <Body2 style={styles.detailLabel}>Due Date</Body2>
                                    <Body2 style={styles.detailValue}>{new Date(activeLoan.due_date).toLocaleDateString()}</Body2>
                                </View>
                                <View style={styles.detailRow}>
                                    <Body2 style={styles.detailLabel}>Interest Rate</Body2>
                                    <Body2 style={styles.detailValue}>{parseFloat(activeLoan.interest_rate)}% Flat</Body2>
                                </View>
                            </View>
                            {activeLoan.status === 'active' && (
                                <TouchableOpacity
                                    style={styles.repayBtn}
                                    onPress={() => {
                                        setRepayAmount(activeLoan.balance.toString());
                                        setShowRepayModal(true);
                                    }}
                                >
                                    <ButtonText style={styles.repayBtnText}>REPAY LOAN</ButtonText>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>
                ) : eligibility?.maxAmount > 0 ? (
                    <View style={styles.section}>
                        <H3 style={styles.sectionTitle}>Apply for a Loan</H3>
                        <View style={styles.formCard}>
                            <Body2 style={styles.inputLabel}>How much do you need?</Body2>
                            <View style={styles.inputWrapper}>
                                <Text style={styles.currencyPrefix}>KSH</Text>
                                <TextInput
                                    style={styles.textInput}
                                    placeholder="0.00"
                                    keyboardType="numeric"
                                    value={amount}
                                    onChangeText={setAmount}
                                />
                            </View>

                            <Body2 style={styles.inputLabel}>Repayment Duration</Body2>
                            <View style={styles.durationTabs}>
                                {['1', '3', '6'].map(d => (
                                    <TouchableOpacity
                                        key={d}
                                        style={[styles.durationTab, duration === d && styles.durationTabActive]}
                                        onPress={() => setDuration(d)}
                                    >
                                        <Text style={[styles.durationTabText, duration === d && styles.durationTabTextActive]}>
                                            {d} Month{d !== '1' ? 's' : ''}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                            </View>

                            <Body2 style={styles.inputLabel}>Reason for Loan</Body2>
                            <TextInput
                                style={[styles.textInput, styles.textArea]}
                                placeholder="e.g., Business expansion, emergency, education..."
                                multiline
                                numberOfLines={3}
                                value={purpose}
                                onChangeText={setPurpose}
                            />

                            <TouchableOpacity
                                style={[styles.applyBtn, applying && { opacity: 0.7 }]}
                                onPress={handleApply}
                                disabled={applying}
                            >
                                {applying ? (
                                    <ActivityIndicator size="small" color="#FFFFFF" />
                                ) : (
                                    <ButtonText style={styles.applyBtnText}>SUBMIT APPLICATION</ButtonText>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>
                ) : (
                    <View style={styles.notEligibleBox}>
                        <Ionicons name="lock-closed" size={48} color="#D1D5DB" />
                        <H3 style={styles.notEligibleTitle}>Loans Locked</H3>
                        <Body2 style={styles.notEligibleDesc}>
                            You don't qualify for a loan just yet. Continue your activity on the platform to build your score.
                        </Body2>
                    </View>
                )}

                {/* Loan History */}
                {loans.filter(l => ['paid', 'rejected', 'defaulted'].includes(l.status)).length > 0 && (
                    <View style={styles.section}>
                        <H3 style={styles.sectionTitle}>Loan History</H3>
                        {loans.filter(l => ['paid', 'rejected', 'defaulted'].includes(l.status)).map((loan, idx) => (
                            <View key={idx} style={styles.historyCard}>
                                <View style={styles.historyInfo}>
                                    <Body1 style={styles.historyAmount}>KSH {parseFloat(loan.amount).toLocaleString()}</Body1>
                                    <Caption style={styles.historyDate}>{new Date(loan.createdAt).toLocaleDateString()}</Caption>
                                </View>
                                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(loan.status) + '15' }]}>
                                    <Text style={[styles.statusText, { color: getStatusColor(loan.status) }]}>
                                        {loan.status.toUpperCase()}
                                    </Text>
                                </View>
                            </View>
                        ))}
                    </View>
                )}

            </ScrollView>

            {/* Repayment Modal */}
            <Modal
                visible={showRepayModal}
                transparent={true}
                animationType="slide"
                onRequestClose={() => setShowRepayModal(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalHeader}>
                            <H3 style={styles.modalTitle}>Repay Loan</H3>
                            <TouchableOpacity onPress={() => setShowRepayModal(false)}>
                                <Ionicons name="close" size={24} color="#6B7280" />
                            </TouchableOpacity>
                        </View>

                        <View style={styles.modalBody}>
                            <Body2 style={styles.inputLabel}>Amount to Pay</Body2>
                            <View style={styles.inputWrapper}>
                                <Text style={styles.currencyPrefix}>KSH</Text>
                                <TextInput
                                    style={styles.textInput}
                                    placeholder="0.00"
                                    keyboardType="numeric"
                                    value={repayAmount}
                                    onChangeText={setRepayAmount}
                                />
                                <TouchableOpacity
                                    style={styles.maxBtn}
                                    onPress={() => setRepayAmount(activeLoan?.balance.toString())}
                                >
                                    <Text style={styles.maxBtnText}>MAX</Text>
                                </TouchableOpacity>
                            </View>

                            <Body2 style={styles.inputLabel}>M-Pesa Phone Number</Body2>
                            <View style={styles.inputWrapper}>
                                <Ionicons name="call-outline" size={20} color="#6B7280" style={{ marginRight: 10 }} />
                                <TextInput
                                    style={styles.textInput}
                                    placeholder="2547XXXXXXXX"
                                    keyboardType="phone-pad"
                                    value={repayPhone}
                                    onChangeText={setRepayPhone}
                                />
                            </View>

                            <View style={styles.infoBox}>
                                <Ionicons name="information-circle-outline" size={20} color="#6366F1" />
                                <Body2 style={styles.infoText}>
                                    You will receive an STK Push on this phone to authorize the payment.
                                </Body2>
                            </View>

                            <TouchableOpacity
                                style={[styles.confirmRepayBtn, repaying && { opacity: 0.7 }]}
                                onPress={handleRepay}
                                disabled={repaying}
                            >
                                {repaying ? (
                                    <ActivityIndicator size="small" color="#FFFFFF" />
                                ) : (
                                    <ButtonText style={styles.confirmRepayBtnText}>CONFIRM REPAYMENT</ButtonText>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>
        </View>
    );
};

const ScoreItem = ({ label, score, icon, color }) => (
    <View style={styles.scoreItem}>
        <View style={[styles.scoreIcon, { backgroundColor: color + '15' }]}>
            <Ionicons name={icon} size={20} color={color} />
        </View>
        <H3 style={styles.scoreValue}>{score}</H3>
        <Caption style={styles.scoreLabel}>{label}</Caption>
    </View>
);

const getStatusColor = (status) => {
    switch (status) {
        case 'active': return '#10B981';
        case 'pending': return '#F59E0B';
        case 'paid': return '#6366F1';
        case 'rejected': return '#EF4444';
        default: return '#6B7280';
    }
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    header: {
        paddingHorizontal: 16,
        paddingBottom: 20,
    },
    headerTop: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 14,
    },
    backBtn: {
        width: 34,
        height: 34,
        borderRadius: 17,
        backgroundColor: 'rgba(255,255,255,0.2)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitle: {
        color: '#FFFFFF',
        fontSize: 17,
    },
    eligibilityCard: {
        backgroundColor: 'rgba(255,255,255,0.15)',
        borderRadius: 18,
        padding: 14,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
        alignItems: 'center',
    },
    eligLabel: {
        color: 'rgba(255,255,255,0.8)',
        letterSpacing: 1.5,
        fontSize: 10,
        marginBottom: 3,
    },
    eligStatus: {
        color: '#FFFFFF',
        fontSize: 28,
        marginBottom: 10,
    },
    limitBox: {
        backgroundColor: 'rgba(0,0,0,0.2)',
        paddingHorizontal: 16,
        paddingVertical: 7,
        borderRadius: 12,
        alignItems: 'center',
    },
    limitLabel: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 9,
    },
    limitValue: {
        color: '#FFFFFF',
        fontSize: 16,
    },
    content: {
        flex: 1,
        paddingHorizontal: 16,
        marginTop: 16,
    },
    advicePanel: {
        flexDirection: 'row',
        backgroundColor: '#FFFBEB',
        padding: 12,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#FEF3C7',
        alignItems: 'center',
        marginBottom: 18,
        ...getShadow('sm'),
    },
    adviceIcon: {
        width: 38,
        height: 38,
        borderRadius: 19,
        backgroundColor: '#FEF3C7',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 10,
    },
    adviceTextContainer: {
        flex: 1,
    },
    adviceTitle: {
        fontSize: 14,
        color: '#92400E',
        marginBottom: 1,
    },
    adviceText: {
        color: '#B45309',
        fontSize: 12,
        lineHeight: 16,
    },
    section: {
        marginBottom: 18,
    },
    sectionTitle: {
        fontSize: 15,
        marginBottom: 10,
        color: '#111827',
    },
    breakdownGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    scoreItem: {
        width: '31%',
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: 10,
        alignItems: 'center',
        marginBottom: 10,
        borderWidth: 1,
        borderColor: '#F3F4F6',
        ...getShadow('xs'),
    },
    scoreIcon: {
        width: 34,
        height: 34,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 6,
    },
    scoreValue: {
        fontSize: 15,
        color: '#111827',
    },
    scoreLabel: {
        color: '#6B7280',
        fontSize: 9,
    },
    formCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        padding: 16,
        borderWidth: 1,
        borderColor: '#F3F4F6',
        ...getShadow('sm'),
    },
    inputLabel: {
        color: '#374151',
        marginBottom: 6,
        fontWeight: '600',
        fontSize: 13,
    },
    inputWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F9FAFB',
        borderRadius: 12,
        paddingHorizontal: 12,
        marginBottom: 14,
        borderWidth: 1,
        borderColor: '#E5E7EB',
    },
    currencyPrefix: {
        fontSize: 14,
        fontWeight: '800',
        color: '#6B7280',
        marginRight: 8,
    },
    textInput: {
        flex: 1,
        height: 44,
        fontSize: 15,
        fontWeight: '700',
        color: '#111827',
    },
    textArea: {
        height: 80,
        textAlignVertical: 'top',
        paddingVertical: 12,
        backgroundColor: '#F9FAFB',
        borderRadius: 12,
        paddingHorizontal: 12,
        marginBottom: 14,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        fontSize: 13,
        fontWeight: '400',
    },
    durationTabs: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 14,
    },
    durationTab: {
        flex: 1,
        height: 38,
        borderRadius: 10,
        backgroundColor: '#F3F4F6',
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: 3,
    },
    durationTabActive: {
        backgroundColor: '#10B981',
    },
    durationTabText: {
        fontSize: 12,
        color: '#6B7280',
        fontWeight: '600',
    },
    durationTabTextActive: {
        color: '#FFFFFF',
    },
    applyBtn: {
        backgroundColor: '#10B981',
        height: 46,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 6,
    },
    applyBtnText: {
        color: '#FFFFFF',
        fontSize: 13,
        fontWeight: '800',
    },
    loanCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        padding: 16,
        borderWidth: 1,
        borderColor: '#F3F4F6',
        ...getShadow('sm'),
    },
    loanCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 14,
    },
    loanLabel: {
        color: '#6B7280',
        fontSize: 12,
    },
    loanValue: {
        fontSize: 22,
        color: '#111827',
    },
    statusBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 8,
    },
    statusText: {
        fontSize: 9,
        fontWeight: '800',
    },
    loanDetails: {
        backgroundColor: '#F9FAFB',
        borderRadius: 12,
        padding: 12,
        marginBottom: 14,
    },
    detailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 6,
    },
    detailLabel: {
        color: '#6B7280',
        fontSize: 13,
    },
    detailValue: {
        color: '#111827',
        fontWeight: '600',
        fontSize: 13,
    },
    repayBtn: {
        backgroundColor: '#6366F1',
        height: 42,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    repayBtnText: {
        color: '#FFFFFF',
        fontSize: 13,
        fontWeight: '800',
    },
    notEligibleBox: {
        alignItems: 'center',
        padding: 30,
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        marginTop: 16,
    },
    notEligibleTitle: {
        marginTop: 12,
        marginBottom: 8,
        color: '#374151',
        fontSize: 15,
    },
    notEligibleDesc: {
        textAlign: 'center',
        color: '#6B7280',
        lineHeight: 18,
        fontSize: 13,
    },
    historyCard: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        padding: 12,
        borderRadius: 14,
        marginBottom: 8,
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    historyInfo: {
        flex: 1,
    },
    historyAmount: {
        fontSize: 14,
        fontWeight: '700',
        color: '#111827',
    },
    historyDate: {
        color: '#9CA3AF',
        marginTop: 1,
        fontSize: 11,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'flex-end',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        padding: 20,
        minHeight: 350,
        ...getShadow('lg'),
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 18,
    },
    modalTitle: {
        fontSize: 17,
        color: '#111827',
    },
    modalBody: {
        marginBottom: 16,
    },
    maxBtn: {
        backgroundColor: '#E5E7EB',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 6,
    },
    maxBtnText: {
        fontSize: 9,
        fontWeight: '800',
        color: '#374151',
    },
    infoBox: {
        flexDirection: 'row',
        backgroundColor: '#EEF2FF',
        padding: 12,
        borderRadius: 12,
        alignItems: 'center',
        marginBottom: 18,
    },
    infoText: {
        flex: 1,
        marginLeft: 10,
        fontSize: 12,
        color: '#4338CA',
        lineHeight: 16,
    },
    confirmRepayBtn: {
        backgroundColor: '#10B981',
        height: 46,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 6,
        ...getShadow('sm'),
    },
    confirmRepayBtnText: {
        color: '#FFFFFF',
        fontSize: 13,
        fontWeight: '800',
    },
});

export default LoanScreen;
